import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useDivisionFromQuery } from "../hooks/useDivisionFromQuery";

export default function CompetitionPage() {
  useDivisionFromQuery();

  return (
    <div className="app-container">
      <Header />

      <main className="main-content content-page">
        <h2>Competition Information</h2>

        <div className="info-section">
          <h3>About the Competition</h3>
          <p>
            A nationwide performance competition for in-store clerks.{" "}
            <strong>No judging and no random draws.</strong> Winners are based
            solely on the number of verified customer redemptions of Proper Wild
            shots at your store, tracked by our partner Aisle.
          </p>
          <p>
            Prizes are paid to the store, and stores decide how to distribute to
            clerks.
          </p>
        </div>

        <div className="info-section">
          <h3>Division Structure</h3>
          <p>
            Stores compete in fair divisions based on chain size. Everyone plays
            in their appropriate division to ensure fair competition among
            similar-sized retailers.
          </p>
          <p className="note">
            Chain opt-in is required. Once a chain opts in, all stores in that
            chain are automatically enrolled. Opt-in is not a requirement for
            distribution.
          </p>
        </div>

        <div className="info-section">
          <h3>Prizes and Rewards</h3>
          <p>
            Prizes are issued to the store (USD). Stores are responsible for any
            distribution to clerks and tax handling. Multiple prizes can be
            earned by the same store:
          </p>

          <h4>Milestone Prizes</h4>
          <ul>
            <li>
              <strong>50 verified redemptions:</strong> $50
            </li>
            <li>
              <strong>100 verified redemptions:</strong> $75 (in addition to the
              $50)
            </li>
          </ul>

          <h4>Speed Bonus</h4>
          <ul>
            <li>
              <strong>First 10 stores to reach 100 redemptions</strong> (per
              division): $250
            </li>
          </ul>

          <h4>Performance Prizes</h4>
          <ul>
            <li>
              <strong>Top 100 stores</strong> (per division): $250 each (must
              have ≥50 redemptions)
            </li>
            <li>
              <strong>1st Place:</strong> $6,000 (must have ≥50 redemptions)
            </li>
            <li>
              <strong>2nd Place:</strong> $3,000 (must have ≥50 redemptions)
            </li>
            <li>
              <strong>3rd Place:</strong> $1,000 (must have ≥50 redemptions)
            </li>
          </ul>

          <p className="note">
            <strong>Example:</strong> A store that hits 100 redemptions early
            and finishes in the divisional Top 3 could earn: $50 + $75 + $250
            (Speed Bonus) + Top 3 award.
          </p>
        </div>

        <div className="info-section">
          <h3>How Redemptions Work</h3>
          <p>
            A redemption occurs when a customer completes the Aisle receipt flow
            for a Proper Wild shot purchased at your store during the
            competition window.
          </p>
          <ul>
            <li>Aisle is the system of record for counts and timestamps</li>
            <li>Receipts must clearly show store name and Proper Wild</li>
            <li>
              Invalid, duplicate, illegible, or out-of-window receipts don't
              count
            </li>
            <li>
              All verified redemptions count toward your store total (even if a
              particular purchase isn't reimbursed)
            </li>
          </ul>
        </div>

        <div className="info-section">
          <h3>Customer Redemption Process</h3>
          <p>
            Encourage your customers to participate using these simple steps:
          </p>
          <ol>
            <li>
              Go to:{" "}
              <a
                href="https://try.gotoaisle.com/proper-wild-in-store"
                target="_blank"
                rel="noopener noreferrer"
              >
                try.gotoaisle.com/proper-wild-in-store
              </a>
            </li>
            <li>Enter phone number to receive an intro text</li>
            <li>Buy a Proper Wild shot in-store</li>
            <li>
              Text a photo of the receipt (showing Proper Wild and store name)
              to the Aisle number
            </li>
            <li>
              Get reimbursed via Venmo or PayPal for the first shot; then every
              5th shot is free. Plus they're entered for a chance to win
              $20,000!
            </li>
          </ol>
          <p className="note">
            Having trouble? Customers can text SUPPORT to the same number used
            to submit receipts.
          </p>
        </div>

        <div className="info-section">
          <h3>How to Participate</h3>
          <ul>
            <li>
              Ensure your chain has opted in and your store is assigned to a
              division
            </li>
            <li>
              Promote Proper Wild energy shots to customers throughout the
              competition period
            </li>
            <li>
              Encourage customers to use the Aisle redemption flow (see above)
            </li>
            <li>
              Track your store's progress on the leaderboard (updated hourly)
            </li>
            <li>
              Use the provided <a href="/talking-points">talking points</a> to
              help educate customers about Proper Wild benefits
            </li>
          </ul>
        </div>

        <div className="info-section">
          <h3>Rules and Eligibility</h3>
          <ul>
            <li>U.S. and D.C. only. Void where prohibited</li>
            <li>
              No judging and no random draws; winners are determined by verified
              redemption counts only
            </li>
            <li>
              Each redemption is tracked via the Aisle platform and validated
              automatically
            </li>
            <li>
              Fraudulent activity or gaming the system will result in
              disqualification
            </li>
            <li>
              Proper Wild reserves the right to verify redemptions, adjust
              rankings, and re-classify divisions if necessary
            </li>
            <li>
              In case of ties, the store with the earlier Aisle timestamp of the
              last qualifying redemption ranks higher
            </li>
            <li>
              Subject to{" "}
              <a
                href="https://bit.ly/PWClerkRules"
                target="_blank"
                rel="noopener noreferrer"
              >
                Official Rules
              </a>{" "}
              &{" "}
              <a
                href="https://bit.ly/PWClerkTC"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and Conditions
              </a>
            </li>
          </ul>
        </div>

        <div className="info-section">
          <h3>Helpful Links</h3>
          <ul>
            <li>
              <strong>Consumer Redemption:</strong>{" "}
              <a
                href="https://try.gotoaisle.com/proper-wild-in-store"
                target="_blank"
                rel="noopener noreferrer"
              >
                try.gotoaisle.com/proper-wild-in-store
              </a>
            </li>
            <li>
              <strong>More Info:</strong>{" "}
              <a
                href="https://mandysmillion.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                MandysMillion.com
              </a>
            </li>
            <li>
              <strong>Questions:</strong>{" "}
              <a href="mailto:support@properwild.com">support@properwild.com</a>
            </li>
          </ul>
        </div>

        <div className="info-section">
          <h3>Questions?</h3>
          <p>
            For more information about the competition, check out our{" "}
            <a href="/faqs">FAQs page</a> or contact{" "}
            <a href="mailto:support@properwild.com">support@properwild.com</a>.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
